import * as Sentry from '@sentry/react';
import { message } from 'antd';
import type { ApiError, ApiErrorDetail } from 'common/models';
import ErrorMessage from 'components/ErrorAlert/ErrorMessage';
import type { ErrorOption, FieldValues, UseFormSetError } from 'react-hook-form';
import { getI18n } from 'react-i18next';

export const EXPIRED_LINK = 'errors.expiredLink';
export const PUBLIC_ERROR_KEY = 'public_global_error';
export const FORM_ERROR_DETAIL_KEY = '_errorDetail';
export const FORM_ERROR_TRACE_ID_KEY = '_errorTraceId';
export const FORM_ERROR_TITLE_KEY = '_errorTitle';
export const FORM_ERROR_MESSAGE_KEY = '_errorMessage';

export interface FormErrorPayload {
  errorCode: string;
  errorMessage?: string;
  detail?: string;
  traceId?: string;
  title?: string;
}

const getFormErrorPayloadFromApiError = (
  apiError: ApiError,
  fallbackCode?: string
): FormErrorPayload => {
  const data = apiError.response?.data;
  const errorCode = data?.errorCode ?? fallbackCode ?? 'unknown';

  return {
    errorCode,
    errorMessage: data?.errorMessage ?? undefined,
    detail: data?.detail ?? undefined,
    traceId: data?.traceId ?? undefined,
    title: data?.title ?? undefined
  };
};

const transformErrors = (errors: Record<string, string[]>) => {
  const newErrors: Record<string, string> = {};

  Object.keys(errors).forEach((e) => {
    const firstError = errors[e][0];
    newErrors[e] = firstError;
  });

  return newErrors;
};

export const getApiErrorCode = (error: ApiError | unknown): string => {
  let errorCode = 'unknown';
  const data = (error as ApiError)?.response?.data;

  if (data?.errorCode) {
    return data.errorCode;
  }

  if (data?.errorMessage) {
    return data.errorMessage;
  }

  if ((error as ApiError)?.message === 'Network Error') {
    return 'network';
  }

  if (!(error as ApiError)?.response) {
    return 'unknown';
  }

  if ((error as ApiError).response!.status === 401) {
    return ''; // no error message
  }

  errorCode = 'unknown';

  if ((error as ApiError).response!.status === 403) {
    errorCode = 'noPermission';
  }

  return errorCode;
};

export const handleApiError = (error: ApiError | unknown) => {
  const axiosError = error as ApiError;
  if (axiosError.response) {
    // Server errors
    if (axiosError.response.status !== 403 && axiosError.response.status !== 401) {
      Sentry.captureException(error);
    }
  } else if (axiosError.message !== 'Network Error') {
    // Local errors
    Sentry.captureException(error);
  }
};

export const showErrorMessage = (errorMessage: string) => {
  if (errorMessage) {
    message.error(errorMessage);
  }
};

export const showApiErrorMessage = (error: ApiError | unknown) => {
  if ((error as ApiError).response?.data) {
    const e = error as ApiError;

    message.error({
      className: 'ErrorMessage_notice',
      content: <ErrorMessage error={e.response!.data!} />,
      duration: 5
    });
  }
};

// ErrorMessage renders title/errorMessage/errorCode/detail - a payload carrying none of them (the
// legacy { _error: "code" } shape) would render an empty toast, i.e. the request would fail with no
// visible feedback at all. Decide here whether there is anything to render.
const hasRenderableDetail = (data?: ApiErrorDetail) =>
  !!(data && (data.title || data.errorMessage || data.errorCode || data.detail));

export const handleApiErrorWithNotification = (error: ApiError | unknown) => {
  const i18n = getI18n();
  handleApiError(error);
  const errorCode = getApiErrorCode(error);
  if (hasRenderableDetail((error as ApiError).response?.data)) {
    showApiErrorMessage(error);
  } else {
    showErrorMessage(i18n.t(errorCode, { ns: 'errors' }));
  }
};

export const processServerFormError = (error: ApiError | unknown) => {
  const apiError = error as ApiError;
  if (apiError.response && apiError.response.status === 400 && apiError.response.data) {
    const data = apiError.response.data;

    const translated = transformErrors(data.errors ?? {});
    if (data.errorMessage) {
      translated[FORM_ERROR_MESSAGE_KEY] = data.errorMessage;
    }
    if (data.errorCode) {
      translated._error = data.errorCode;
    }
    if (data.detail) {
      translated[FORM_ERROR_DETAIL_KEY] = data.detail;
    }
    if (data.traceId) {
      translated[FORM_ERROR_TRACE_ID_KEY] = data.traceId;
    }
    if (data.title) {
      translated[FORM_ERROR_TITLE_KEY] = data.title;
    }
    return translated;
  }
  handleApiError(error);

  const payload = getFormErrorPayloadFromApiError(apiError, getApiErrorCode(error));
  const fallbackErrors: Record<string, string> = {
    _error: payload.errorCode
  };

  if (payload.errorMessage) {
    fallbackErrors[FORM_ERROR_MESSAGE_KEY] = payload.errorMessage;
  }

  if (payload.detail) {
    fallbackErrors[FORM_ERROR_DETAIL_KEY] = payload.detail;
  }
  if (payload.traceId) {
    fallbackErrors[FORM_ERROR_TRACE_ID_KEY] = payload.traceId;
  }
  if (payload.title) {
    fallbackErrors[FORM_ERROR_TITLE_KEY] = payload.title;
  }

  return fallbackErrors;
};

export const setRootFormError = <T extends FieldValues>(
  setError: UseFormSetError<T>,
  message: string,
  types?: ErrorOption['types']
) => {
  setError('root', {
    type: 'manual',
    message,
    types
  });
};

export const handleFormServerErrors = <T extends FieldValues>(
  e: unknown,
  setError: (
    name: keyof T,
    error: ErrorOption,
    options?: {
      shouldFocus: boolean;
    }
  ) => void
) => {
  const { t } = getI18n();

  const error = e as ApiError;

  // wrong login credentials
  if (error.response && error.response.status === 403 && error.name === '_error' && error.message) {
    return setError('root', {
      type: 'manual',
      message: t(error.message, { ns: 'errors' })
    });
  }

  if (error.response && error.response.status === 400 && error.response.data) {
    const { errorMessage, errors, errorCode } = error.response.data;
    const payload = getFormErrorPayloadFromApiError(error, errorCode ?? getApiErrorCode(error));

    if (errorMessage) {
      setError('root', {
        type: 'manual',
        message: errorMessage,
        types: {
          detail: payload.detail,
          traceId: payload.traceId,
          title: payload.title,
          errorCode: payload.errorCode,
          errorMessage: payload.errorMessage
        }
      });
    } else if (payload.errorCode || payload.title || payload.detail || payload.traceId) {
      setError('root', {
        type: 'manual',
        message: t(payload.errorCode || 'unknown', { ns: 'errors' }),
        types: {
          detail: payload.detail,
          traceId: payload.traceId,
          title: payload.title,
          errorCode: payload.errorCode,
          errorMessage: payload.errorMessage
        }
      });
    }

    if (errors) {
      Object.keys(errors).forEach((key) => {
        setError(key as keyof T, {
          type: 'manual',
          message: errors[key][0]
        });
      });
    }
  } else {
    handleApiError(error);
    const payload = getFormErrorPayloadFromApiError(error, getApiErrorCode(error));

    return setError('root', {
      type: 'manual',
      message: payload.errorMessage || t(payload.errorCode || 'unknown', { ns: 'errors' }),
      types: {
        detail: payload.detail,
        traceId: payload.traceId,
        title: payload.title,
        errorCode: payload.errorCode,
        errorMessage: payload.errorMessage
      }
    });
  }
};
